import React, { useState, useEffect } from 'react'; // Import useEffect
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { gql, useMutation } from '@apollo/client';

const ONBOARD_VEHICLE = gql`
  mutation Mutation($chassisNo: String!, $engineNo: String!, $imei: String!) {
    onboardVehicle(chassisNo: $chassisNo, engineNo: $engineNo, IMEI: $imei) {
      status
      message
    }
  }
`;

interface MapVinDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MapVinDialog: React.FC<MapVinDialogProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    vin: '',
    engineNo: '',
    imei: ''
  });
  const [errors, setErrors] = useState({
    vin: '',
    engineNo: '',
    imei: ''
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });
  const [isFormValid, setIsFormValid] = useState(false); // New state for form validity
  const [onboardVehicle] = useMutation(ONBOARD_VEHICLE);

  // Effect to check form validity whenever formData changes
  useEffect(() => {
    setIsFormValid(
      formData.vin.length <= 20 &&
      formData.engineNo.length > 0 && // Changed from !formData.engineNo to check length for consistency
      formData.imei.length <= 15
    );
  }, [formData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {
      vin: '',
      engineNo: '',
      imei: ''
    };

    if (!formData.vin) {
      newErrors.vin = 'VIN is required';
      valid = false;
    } else if (formData.vin.length > 20) {
      newErrors.vin = 'VIN must be 20 characters or less';
      valid = false;
    }

    if (!formData.engineNo) {
      newErrors.engineNo = 'Engine No is required';
      valid = false;
    }

    if (!formData.imei) {
      newErrors.imei = 'IMEI is required';
      valid = false;
    } else if (formData.imei.length > 15) {
      newErrors.imei = 'IMEI must be 15 digits or less';
      valid = false;
    }


    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data } = await onboardVehicle({
        variables: {
          chassisNo: formData.vin,
          engineNo: formData.engineNo,
          imei: formData.imei
        }
      });

      if (data?.onboardVehicle?.status) {
        setSnackbar({
          open: true,
          message: 'Vehicle onboarded successfully',
          severity: 'success'
        });

        // Show success message for 2 seconds before closing
        setTimeout(() => {
          if (onSuccess) onSuccess();
          handleClose();
        }, 2000);
      } else {
        setSnackbar({
          open: true,
          message: data?.onboardVehicle?.message || 'Failed to onboard vehicle',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'An error occurred while onboarding vehicle',
        severity: 'error'
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleClose = () => {
    setFormData({
      vin: '',
      engineNo: '',
      imei: ''
    });
    setErrors({
      vin: '',
      engineNo: '',
      imei: ''
    });
    setLoading(false); // Reset loading state on close
    setIsFormValid(false); // Reset form validity on close
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          width: '100%',
          maxWidth: '400px',
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#ffffffff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          VIN-IMEI Mapping
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: '#000',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px', maxHeight: '50vh', overflowY: 'auto' }}>
        <Grid container spacing={2}>
          {/* VIN Field */}
          <Grid sx={{ xs: 12, sm: 6, md: 4, width: '100%' }}> {/* Changed sx to item for correct Grid usage */}
            <TextField
              fullWidth
              margin="normal"
              label="VIN (Chassis No)"
              name="vin"
              value={formData.vin}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
                setFormData(prev => ({ ...prev, vin: value }));
                if (errors.vin) setErrors(prev => ({ ...prev, vin: '' }));
              }}
              required
              error={!!errors.vin}
              helperText={errors.vin}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>

          {/* Engine No Field */}
          <Grid sx={{ xs: 12, sm: 6, md: 4, width: '100%' }}> {/* Changed sx to item for correct Grid usage */}
            <TextField
              fullWidth
              margin="normal"
              label="Engine No"
              name="engineNo"
              value={formData.engineNo}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 20);
                setFormData(prev => ({ ...prev, engineNo: value }));
                if (errors.engineNo) setErrors(prev => ({ ...prev, engineNo: '' }));
              }}
              required
              error={!!errors.engineNo}
              helperText={errors.engineNo}
              inputProps={{ maxLength: 20 }}
            />
          </Grid>

          {/* IMEI Field */}
          <Grid sx={{ xs: 12, sm: 6, md: 4, width: '100%' }}> {/* Changed sx to item for correct Grid usage */}
            <TextField
              fullWidth
              margin="normal"
              label="IMEI"
              name="imei"
              value={formData.imei}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 15);
                setFormData(prev => ({ ...prev, imei: value }));
                if (errors.imei) setErrors(prev => ({ ...prev, imei: '' }));
              }}
              required
              error={!!errors.imei}
              helperText={errors.imei}
              inputProps={{ maxLength: 15 }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{
        padding: '16px 24px',
        justifyContent: 'flex-end'
      }}>
        <Button
          onClick={handleClose}
          variant='outlined'
        >
          CANCEL
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color='error'
          disabled={loading || !isFormValid}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'SUBMIT'}
        </Button>
      </DialogActions>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000} // Show for 2 seconds
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Dialog>
  );
};

export default MapVinDialog;